/**
 * SessionActivityService
 *
 * Uses the browser's BroadcastChannel API to synchronise session activity
 * across multiple tabs opened by the same user.  When the user keeps their
 * session alive in one tab (e.g. by clicking "Stay signed in") the event is
 * broadcast to all other tabs so they can also reset their countdown timers.
 *
 * If BroadcastChannel is unavailable (e.g. in older browsers or certain test
 * environments) the service degrades gracefully – `logActivity` and
 * `onActivity` become no-ops.
 */
class SessionActivityService {
    /**
     * @param {typeof BroadcastChannel|null} BrowserBroadcastChannel
     *   Pass `window.BroadcastChannel` in production.  Pass `null` or
     *   `undefined` to disable cross-tab synchronisation (useful in tests).
     */
    constructor(BrowserBroadcastChannel) {
        this.activityChannel = BrowserBroadcastChannel && new BrowserBroadcastChannel('session-activity');
    }

    /**
     * Broadcast a timestamped activity event to all other tabs.
     * No-op when the channel is not available.
     */
    logActivity() {
        if (this.activityChannel) {
            const event = { timestamp: Date.now() };
            this.activityChannel.postMessage(event);
        }
    }

    /**
     * Register a callback that is invoked whenever another tab broadcasts
     * a session-activity event.
     *
     * @param {Function} callback - Receives the event's `data` payload
     *   (`{ timestamp: number }`).
     */
    onActivity(callback) {
        if (this.activityChannel) {
            this.activityChannel.onmessage = (event) => {
                callback(event.data);
            };
        }
    }
}

export default SessionActivityService;
